import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractService } from '../../commons/abstract.service';
import {
  ChatBootstrapData,
  ChatBootstrapMeta,
  ChatChannelMemberInfo,
  ChatChannelMembersMeta,
  ChatDiscoveredChannel,
  ChatDiscoverMeta,
  ChatDiscoverParams,
  ChatEnvelope,
  ChatMessagesMeta,
  ChatMessagesParams,
  ChatSavedMessages,
  ChatSavedMeta,
  ChatSearchData,
  ChatSearchMeta,
  ChatSearchParams,
  ChatThreadData,
  ChatThreadListItem,
  ChatThreadsMeta,
} from '../../models/chat/chat-api.model';
import { ChatChannel } from '../../models/chat/chat-channel.model';
import { ChatMessage } from '../../models/chat/chat-message.model';

/**
 * Internal Chat read-only REST client (SPEC-2026-07-internal-chat, T-16).
 *
 * Mirrors trendmulti-api/src/modules/chat (T-14). READS ONLY — every chat
 * write goes through the websocket command path (ChatSocketService); this
 * service never mutates chat data.
 *
 * All endpoints answer the `{ data, meta, error }` envelope, returned WHOLE so
 * callers consume `data` and `meta` (nextCursor / userDisplay) together.
 * Message cursors are opaque — echo `meta.nextCursor` verbatim.
 */
@Injectable({ providedIn: 'root' })
export class ChatService extends AbstractService<ChatChannel> {

  getURLBase(): string {
    return '/chat';
  }

  constructor(http: HttpClient) {
    super(http);
  }

  /** Chat data is never created over REST — websocket command path only. */
  preCreate(obj: ChatChannel): void { }

  /** Chat data is never updated over REST — websocket command path only. */
  preUpdate(obj: ChatChannel): void { }

  /** GET /api/chat/bootstrap — sidebar snapshot (conversations, favorites, unread, presence, statuses). */
  getBootstrap(): Observable<ChatEnvelope<ChatBootstrapData, ChatBootstrapMeta>> {
    return super.get('/bootstrap');
  }

  /** GET /api/chat/channels/discover — paginated PUBLIC channel directory. */
  discoverChannels(params: ChatDiscoverParams = {}): Observable<ChatEnvelope<ChatDiscoveredChannel[], ChatDiscoverMeta>> {
    return super.getWithParams('/channels/discover', this.compactParams(params));
  }

  /**
   * GET /api/chat/channels/:id/members — active roster with conversation-level
   * roles. PUBLIC rosters are company-readable; other types require membership
   * (server-enforced). Display data rides in `meta.userDisplay`.
   */
  getChannelMembers(channelId: string): Observable<ChatEnvelope<ChatChannelMemberInfo[], ChatChannelMembersMeta>> {
    return super.get(`/channels/${channelId}/members`);
  }

  /**
   * GET /api/chat/channels/:id/messages — cursor-paginated history.
   * `before` pages back through history; `after` fetches the reconnection
   * delta (everything newer than the cursor, ASC). Cursors come from
   * `meta.nextCursor` and are opaque.
   */
  getMessages(channelId: string, params: ChatMessagesParams = {}): Observable<ChatEnvelope<ChatMessage[], ChatMessagesMeta>> {
    return super.getWithParams(`/channels/${channelId}/messages`, this.compactParams(params));
  }

  /** GET /api/chat/messages/:id/thread — parent + cursor-paginated replies. */
  getThread(messageId: string, params: ChatMessagesParams = {}): Observable<ChatEnvelope<ChatThreadData, ChatMessagesMeta>> {
    return super.getWithParams(`/messages/${messageId}/thread`, this.compactParams(params));
  }

  /**
   * GET /api/chat/threads — "Mensagens de Tópico": the threads I participate
   * in (rooted or replied), newest activity first, with the latest replies and
   * the source conversation riding each item.
   */
  getThreads(params: ChatDiscoverParams = {}): Observable<ChatEnvelope<ChatThreadListItem[], ChatThreadsMeta>> {
    return super.getWithParams('/threads', this.compactParams(params));
  }

  /** GET /api/chat/saved — my bookmarks (message/channel populated). */
  getSaved(): Observable<ChatEnvelope<ChatSavedMessages, ChatSavedMeta>> {
    return super.get('/saved');
  }

  /** GET /api/chat/search — messages/channels/people within the caller's allowlist. */
  search(params: ChatSearchParams = {}): Observable<ChatEnvelope<ChatSearchData, ChatSearchMeta>> {
    return super.getWithParams('/search', this.compactParams(params));
  }

  /** Drops undefined/null/empty values so HttpParams never serializes "undefined". */
  private compactParams(params: object): Record<string, string> {
    const compacted: Record<string, string> = {};
    Object.keys(params || {}).forEach((key) => {
      const value = (params as Record<string, unknown>)[key];
      if (value !== undefined && value !== null && value !== '') {
        compacted[key] = String(value);
      }
    });
    return compacted;
  }
}
